import { useEffect, useMemo, useRef, useState } from 'react'
import * as d3 from 'd3'
import { useAuth0 } from '@auth0/auth0-react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import pick from 'lodash/pick'
import EllipsisCell from '../../../components/DataTable/EllipsisCell.component'
import { formatDate } from '../../../utils/formatting'
import {
  DATE_COLUMN_WIDTH,
  ID_COLUMN_WIDTH,
  NAME_COLUMN_MAX_WIDTH,
} from '../../../components/DataTable/DataTable.constants'

import { AppLayoutContext } from '../../../providers/AppLayout'
import { useUrlStateParams } from '../../../utils/url'
import { APP_UPDATE_NOTICE, SnackbarContext } from '../../../providers/SnackbarContext'

import { AppPermissions } from '../../../models/AppPermissions.model'
import { Sponsor, sponsorFields } from '../../../models/Sponsor.model'
import { SponsorRelations } from '../../../models/SponsorRelations.model'
import { Trail } from '../../../models/Trail.model'

import UserService from '../../../services/UserService/UserService'
import { SponsorService } from '../../../services/SponsorService/SponsorService'
import { TrailSystemService } from '../../../services/TrailSystemService/TrailSystemService'
import { TrailService } from '../../../services/TrailService/TrailService'

import {
  Box,
  FormControl,
  OutlinedInput,
  InputLabel,
  MenuItem,
  Stack,
  Typography,
  Button,
  InputAdornment,
  IconButton,
  Paper,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import { GridColDef } from '@mui/x-data-grid'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import ImportExportIcon from '@mui/icons-material/ImportExport'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import DataTable from '../../../components/DataTable/DataTable.component'
import Loading from '../../../components/Loading/Loading.component'
import ImportExportDialog, {
  ImportExportDialogProps,
} from '../../../components/ImportExportDialog/ImportExportDialog.component'
import Link from '@mui/material/Link'
import FormDialog from '../../../components/FormDialog/FormDialog.component'
import SponsorForm from '../shared/SponsorForm.component'
import { AppStateContext } from '../../../providers/AppStateContext'

type SponsorSearchProps = {
  nested?: boolean
  systemId?: number
  trailId?: number
  hideHeader?: boolean
  excludeColumns?: string[]
}
const SponsorSearch = (props: SponsorSearchProps): JSX.Element => {
  const [currentSearchParameters] = useSearchParams()
  const {
    nested,
    systemId = Number(currentSearchParameters.get('trail-system')) || undefined,
    trailId = Number(currentSearchParameters.get('trail')) || undefined,
    hideHeader = !systemId && !trailId,
    excludeColumns = [],
  } = props

  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const { user } = useAuth0()
  const hasWritePermissions = UserService.hasPermissions(user, [AppPermissions.editSponsor])

  const [searchError, setSearchError] = useState('')
  const [rowActionsAnchorEl, setRowActionsAnchorEl] = useState<null | HTMLElement>(null)
  const isActionsMenuOpen = Boolean(rowActionsAnchorEl)
  const [selectedRow, setSelectedRow] = useState<Sponsor | undefined>()
  const [isAdding, setIsAdding] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false)

  const [name, setName] = useUrlStateParams(
    '',
    'name',
    (value) => encodeURIComponent(value.toString()),
    (value) => (value ? decodeURIComponent(value) : ''),
  )
  const nameInputRef = useRef<HTMLInputElement>()
  const [orderBy, setOrderBy] = useState<keyof Sponsor | undefined>('start')
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc' | undefined>('desc')
  const [page, setPage] = useUrlStateParams(
    1,
    'page',
    (value) => value.toString(),
    (value) => (!isNaN(Number(value)) ? Number(value) : 1),
  )
  const [pageSize, setPageSize] = useUrlStateParams(
    12,
    'page-size',
    (value) => value.toString(),
    (value) => (!isNaN(Number(value)) ? Number(value) : 12),
  )

  const [isImportExportOpen, setIsImportExportOpen] = useState(false)

  useEffect(() => {
    if (!nested) {
      AppLayoutContext.setPageName('Sponsors')
    }
  }, [])

  const queryClient = useQueryClient()

  // fetch the trail for use in the add dialog
  const trailService = useMemo(() => new TrailService(), []) // memo to prevent re-creating service on every render
  const { isLoading: isTrailLoading, data: trail } = useQuery(
    ['TrailService.get', { id: trailId, relations: [] }, AppStateContext.getRegion()],
    () => trailService.get({ id: trailId, relations: [] }),
    {
      enabled: !!trailId,
      onError: (err) => {
        console.error(err)
      },
    },
  )

  // fetch the trail system for use in the add dialog
  const trailSystemService = useMemo(() => new TrailSystemService(), []) // memo to prevent re-creating service on every render
  const { isLoading: isTrailSystemLoading, data: trailSystem } = useQuery(
    ['TrailSystemService.get', { id: systemId, relations: [] }, AppStateContext.getRegion()],
    () => trailSystemService.get({ id: systemId, relations: [] }),
    {
      enabled: !!systemId,
      onError: (err) => {
        console.error(err)
      },
    },
  )

  const sponsorService = useMemo(() => new SponsorService(), []) // memo to prevent re-creating service on every render
  const { isLoading: isLoadingSponsors, data: sponsorsResponse } = useQuery(
    [
      'SponsorService.search',
      {
        systemId,
        trailId,
        page,
        pageSize,
        name,
        orderBy,
        orderDirection,
        relations: [SponsorRelations.system, SponsorRelations.trail],
      },
      AppStateContext.getRegion(),
    ],
    () =>
      sponsorService.search({
        systemId,
        trailId,
        page,
        pageSize,
        name,
        orderBy,
        orderDirection,
        relations: [SponsorRelations.system, SponsorRelations.trail],
      }),
    {
      onError: (err) => {
        setSearchError(typeof err === 'string' ? err : 'An error occurred while searching for sponsors.')
        console.error(err)
      },
    },
  )

  useEffect(() => {
    if (sponsorsResponse && page > 1 && (!sponsorsResponse.total || (page - 1) * pageSize >= sponsorsResponse.total)) {
      // page out of range was requested so reset to page 1
      setPage(1)
    }
  }, [sponsorsResponse])

  // consolidate the various loading states into one
  useEffect(() => {
    setIsLoading(isTrailLoading || isTrailSystemLoading || isLoadingSponsors)
  }, [isTrailLoading, isTrailSystemLoading, isLoadingSponsors])

  const handleRowActionsClick = (event: React.MouseEvent<HTMLButtonElement>, sponsor: Sponsor) => {
    setRowActionsAnchorEl(event.currentTarget)
    setSelectedRow(sponsor)
  }
  const handleRowActionsClose = () => {
    setRowActionsAnchorEl(null)
    setSelectedRow(undefined)
  }

  const columns = useMemo(() => {
    const columns: GridColDef<Sponsor>[] = [
      {
        field: 'actions',
        headerName: '',
        width: 60,
        sortable: false,
        renderCell: (params) => (
          <>
            <IconButton
              id={`row-actions-button-${params.row.id}`}
              color="primary"
              aria-label="row actions"
              aria-controls={selectedRow?.id === params.row.id ? 'row-actions-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={selectedRow?.id === params.row.id ? 'true' : undefined}
              onClick={(event) => handleRowActionsClick(event, params.row)}
            >
              <MoreVertIcon />
            </IconButton>
          </>
        ),
      },
      {
        field: 'id',
        headerName: 'ID',
        width: ID_COLUMN_WIDTH,
        renderCell: (params) => (
          <Link color="primary" aria-label="sponsor detail" href={`/sponsors/${params.row.id}`}>
            {params.row.id}
          </Link>
        ),
      },
      {
        field: 'priority',
        headerName: 'Priority',
        width: 100,
        type: 'number',
        // left-aligned so it reads next to the ID instead of floating at the far edge
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'system',
        headerName: 'Assigned To',
        sortable: false, // cant sort by relational fields
        flex: 1,
        minWidth: 130,
        maxWidth: NAME_COLUMN_MAX_WIDTH,
        renderCell: (params) => {
          const assignedTo = [
            params.row.system?.id ? `System: ${params.row.system?.name}` : '',
            params.row.trail?.id ? `Trail: ${params.row.trail?.name}` : '',
          ]
            .filter(Boolean)
            .join(' · ')
          return <EllipsisCell>{assignedTo}</EllipsisCell>
        },
      },
      {
        field: 'start',
        headerName: 'Start',
        width: DATE_COLUMN_WIDTH,
        renderCell: (params) => (
          <EllipsisCell>{params.row.start ? formatDate(params.row.start) : 'Immediately'}</EllipsisCell>
        ),
      },
      {
        field: 'end',
        headerName: 'End',
        width: DATE_COLUMN_WIDTH,
        renderCell: (params) => <EllipsisCell>{params.row.end ? formatDate(params.row.end) : 'Never'}</EllipsisCell>,
      },
      {
        field: 'name',
        headerName: 'Name',
        // flexible widths let the grid fit its container (e.g. the Sponsors tab on a trail system) without scrolling
        flex: 1.2,
        minWidth: 120,
        maxWidth: NAME_COLUMN_MAX_WIDTH,
      },
    ]
    // remove any columns specified to be removed in the excludeColumns prop
    if (excludeColumns) {
      return columns.filter((column) => !excludeColumns.includes(column.field as string))
    }
    return columns
  }, [excludeColumns])

  const handleSubmit = () => {
    setPage(1)
    setName(nameInputRef?.current?.value || '')
  }

  const handleAddClick = () => {
    setIsAdding(true)
  }

  const handleAddCancel = () => {
    setIsAdding(false)
  }

  const handleAddSubmit = () => {
    setIsAdding(false)
  }

  const handleEditClick = () => {
    setIsEditing(true)
  }

  const handleEditCancel = () => {
    setRowActionsAnchorEl(null)
    setSelectedRow(undefined)
    setIsEditing(false)
  }

  const handleEditSubmit = () => {
    setRowActionsAnchorEl(null)
    setSelectedRow(undefined)
    setIsEditing(false)
  }

  const handleDeleteClick = () => {
    // show a confirmation dialog before deleting
    setIsDeleteConfirmationOpen(true)
  }

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false)
    handleRowActionsClose()
  }

  const deleteSponsor = useMutation(
    (mutationParams: { id: number }) => {
      setIsSaving(true)
      return sponsorService.delete(mutationParams.id)
    },
    {
      onSuccess: () => {
        SnackbarContext.show('Sponsor deleted successfully!', 'success', APP_UPDATE_NOTICE)
        setRowActionsAnchorEl(null)
        setSelectedRow(undefined)
        // invalidate all queries to ensure any queries that are using the modified record are updated
        queryClient.invalidateQueries()
        setIsSaving(false)
      },
      onError: (err) => {
        SnackbarContext.show(`Sponsor failed to delete: ${err}`, 'error')
        console.error(err)
        setIsSaving(false)
      },
    },
  )

  const handleDelete = () => {
    if (selectedRow?.id) {
      setIsDeleteConfirmationOpen(false)
      deleteSponsor.mutate({ id: selectedRow.id })
    }
  }

  const openImportExport = () => {
    setIsImportExportOpen(true)
  }

  const handleImportExportClose = () => {
    setIsImportExportOpen(false)
  }

  const handleImport: ImportExportDialogProps['onImport'] = (data: Partial<Trail>[], options) => {
    setIsImportExportOpen(false)
    if (!data) {
      SnackbarContext.show('Failed to read/parse file', 'error')
      console.error('Failed to read/parse file')
    } else {
      setIsSaving(true)
      sponsorService
        .import(data, options)
        .then(() => {
          SnackbarContext.show('Sponsors imported successfully!', 'success', APP_UPDATE_NOTICE)

          // fetch new data
          setSearchError('')
          // invalidate all queries to ensure any queries that are using the modified record are updated
          queryClient.invalidateQueries()
          setIsSaving(false)
        })
        .catch((err) => {
          SnackbarContext.show(`Sponsors failed to import: ${err}`, 'error')
          console.error(err)
          setIsSaving(false)
        })
    }
  }

  const handleExport = () => {
    // do a request to the API to get the data to export with no pagination
    setIsSaving(true)
    setIsImportExportOpen(false)
    sponsorService
      .search({
        systemId,
        trailId,
        pageSize: 0,
        name,
        orderBy,
        orderDirection,
        relations: [SponsorRelations.system, SponsorRelations.trail],
      })
      .then((results) => {
        const exportData = results.data.map((sponsor) => {
          const sponsorToExport: Record<string, unknown> = { ...sponsor }
          // set a prop for system's or trail's id and name and remove the system object prop
          sponsorToExport.systemId = sponsor.system?.id
          sponsorToExport.systemName = sponsor.system?.name
          sponsorToExport.trailId = sponsor.trail?.id
          sponsorToExport.trailName = sponsor.trail?.name
          delete sponsorToExport.system
          delete sponsorToExport.trail

          sponsorToExport.start = sponsorToExport.start instanceof Date ? sponsorToExport.start.toISOString() : ''
          sponsorToExport.end = sponsorToExport.end instanceof Date ? sponsorToExport.end.toISOString() : ''
          sponsorToExport.updatedAt =
            sponsorToExport.updatedAt instanceof Date ? sponsorToExport.updatedAt.toISOString() : ''
          sponsorToExport.createdAt =
            sponsorToExport.createdAt instanceof Date ? sponsorToExport.createdAt.toISOString() : ''

          // return the object with the props in the order we want them to appear in the csv
          const propsToExport: string[] = [...sponsorFields]
          // add system and trail props after id
          propsToExport.splice(propsToExport.indexOf('region') + 1, 0, 'systemId', 'systemName', 'trailId', 'trailName')
          return pick(sponsorToExport, propsToExport)
        })
        const csvData = d3.csvFormat(exportData)
        // initiate a download of the csv file
        const blob = new Blob([csvData], { type: 'text/csv' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')

        link.setAttribute('href', url)
        link.setAttribute('download', 'sponsors.csv')
        link.click()
        setIsSaving(false)
      })
      .catch((err) => {
        SnackbarContext.show(`Failed to fetch sponsors for export: ${err}`, 'error')
        console.error(err)
      })
  }

  return (
    <>
      {selectedRow && (
        <FormDialog<Sponsor> open={isEditing} entity={selectedRow} entityLabel="Sponsor" onClose={handleEditCancel}>
          <SponsorForm sponsor={selectedRow} onEdit={handleEditSubmit} onCancel={handleEditCancel} />
        </FormDialog>
      )}
      <FormDialog<Sponsor> open={isAdding} entityLabel="Sponsor" onClose={handleAddCancel}>
        <SponsorForm trail={trail} trailSystem={trailSystem} onAdd={handleAddSubmit} onCancel={handleAddCancel} />
      </FormDialog>
      <Dialog fullWidth maxWidth="xs" open={isDeleteConfirmationOpen} onClose={handleDeleteConfirmationClose}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent dividers>
          Are you sure you want to delete sponsor id {selectedRow?.id}: {selectedRow?.name}?
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleDeleteConfirmationClose}>
            Cancel
          </Button>
          <Button onClick={handleDelete}>Confirm</Button>
        </DialogActions>
      </Dialog>
      <ImportExportDialog
        open={isImportExportOpen}
        disableImport={!hasWritePermissions}
        exampleImportFileUrl="/example-import-files/sponsors.csv"
        importFields={[
          ...sponsorFields.filter((field) => !['system', 'trail', 'updatedAt', 'createdAt'].includes(field)),
          'systemId',
          'trailId',
        ]}
        onImport={handleImport}
        onExport={handleExport}
        onClose={handleImportExportClose}
      />
      <Paper sx={{ p: 2 }}>
        {!hideHeader && (
          <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
            {trailSystem?.name ? `${trailSystem.name} ` : trail?.name ? `${trail.name} ` : ''}Sponsors
          </Typography>
        )}
        {(isLoading || isSaving) && !searchError && <Loading sx={{ py: 20 }} />}
        {searchError && (
          <Stack direction="row" justifyContent="center" alignItems="center" sx={{ py: 20 }}>
            <Typography variant="error">{searchError}</Typography>
          </Stack>
        )}
        {!isLoading && !isSaving && !searchError && (
          <>
            <Box sx={{ pt: 1, mb: 1 }}>
              <Stack direction={{ zero: 'column', md: 'row' }} spacing={1}>
                <FormControl variant="outlined" sx={{ width: '100%' }}>
                  <InputLabel>Name</InputLabel>
                  <OutlinedInput
                    id="search-name"
                    label="Name"
                    defaultValue={name || ''}
                    inputRef={nameInputRef}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSubmit()
                      }
                    }}
                    endAdornment={
                      nameInputRef?.current?.value ? (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="clear search name"
                            onClick={() => {
                              if (nameInputRef?.current?.value) {
                                nameInputRef.current.value = ''
                              }
                              setPage(1)
                              setName('')
                            }}
                            edge="end"
                          >
                            <CloseIcon />
                          </IconButton>
                        </InputAdornment>
                      ) : undefined
                    }
                  />
                </FormControl>

                <Button
                  variant="outlined"
                  onClick={handleSubmit}
                  sx={(theme) => ({ [theme.breakpoints.up('md')]: { width: 150 }, maxWidth: '100%' })}
                >
                  Search
                </Button>
              </Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  {hasWritePermissions && (!systemId || (systemId && trailSystem)) && (
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      color="primary"
                      aria-label="add new"
                      onClick={handleAddClick}
                    >
                      Add
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    startIcon={<ImportExportIcon />}
                    color="primary"
                    aria-label="show import export"
                    onClick={() => {
                      openImportExport()
                    }}
                  >
                    Import / Export
                  </Button>
                </Stack>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => {
                    if (nameInputRef?.current?.value) {
                      nameInputRef.current.value = ''
                    }
                    setPage(1)
                    setName('')
                  }}
                >
                  Clear
                </Button>
              </Stack>
            </Box>

            {sponsorsResponse?.data && sponsorsResponse.data.length > 0 ? (
              <>
                <Menu
                  id="row-actions-menu"
                  anchorEl={rowActionsAnchorEl}
                  open={isActionsMenuOpen}
                  onClose={handleRowActionsClose}
                  MenuListProps={{
                    'aria-labelledby': `row-actions-button${selectedRow?.id}`,
                  }}
                >
                  <MenuItem href={`/sponsors/${selectedRow?.id}`} component={Link}>
                    View
                  </MenuItem>
                  {hasWritePermissions && <MenuItem onClick={handleEditClick}>Edit</MenuItem>}
                  {hasWritePermissions && <MenuItem onClick={handleDeleteClick}>Delete</MenuItem>}
                </Menu>
                <DataTable
                  columns={columns}
                  data={sponsorsResponse.data}
                  total={sponsorsResponse.total}
                  paginationMode="server"
                  page={page}
                  pageSize={pageSize}
                  onPageChange={(newPage) => {
                    setPage(newPage)
                  }}
                  onPageSizeChange={(newPageSize) => {
                    setPageSize(newPageSize)
                  }}
                  orderBy={orderBy}
                  orderDirection={orderDirection}
                  onOrderChange={(newOrder) => {
                    setOrderBy(newOrder.field ? (newOrder.field as keyof Sponsor) : undefined)
                    setOrderDirection(newOrder.sort)
                  }}
                />
              </>
            ) : (
              <Box sx={{ p: 2 }}>
                <Typography>No results found</Typography>
              </Box>
            )}
          </>
        )}
      </Paper>
    </>
  )
}
export default SponsorSearch
